import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { handleLoginSuccess, handleLogoutSuccess, handleSessionRefreshError } from '../utils';
import { NavigateFunction } from 'react-router-dom';
import { companyService } from '@/services/companyService';
import { useWidget } from '@/contexts/WidgetContext';
import { recordLoginSession, recordLogoutSession } from '../sessionTracking';
import { useQueryClient } from '@tanstack/react-query';

export const useAuthMethods = (
  setCurrentUser: (user: null) => void,
  setAuthLoading: (loading: boolean) => void,
  setSessionExpireTime: (time: number | null) => void,
  setIsLoggingOut: (isLoggingOut: boolean) => void
) => {
  const { companyId: widgetCompanyId, isWidget } = useWidget();
  const queryClient = useQueryClient();

  const refreshSession = async () => {
    console.log("Manually refreshing session");
    setAuthLoading(true);

    try {
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        console.error("Error refreshing session:", error);
        handleSessionRefreshError();
        throw error;
      }

      if (data.session) {
        console.log("Session refreshed successfully");
        console.log("New expiration:", new Date(data.session.expires_at * 1000).toLocaleString());
        setSessionExpireTime(data.session.expires_at);
        toast({
          title: "Session refreshed",
          description: "Your session has been successfully refreshed."
        });
        // The onAuthStateChange handler will update the user state
      } else {
        console.error("No session returned after refresh");
        toast({
          title: "Authentication Error",
          description: "Your session could not be refreshed. Please log in again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Unexpected error during session refresh:", error);
    } finally {
      setAuthLoading(false);
    }
  };

  const login = async (
    email: string,
    password: string,
    navigate: NavigateFunction,
    redirectTo: string
  ) => {
    console.log("Attempting login for:", email);
    setAuthLoading(true);

    try {
      // Attempt sign-in
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error("Login error:", error);

        // Handle specific error types
        if (error.message.includes('Email not confirmed')) {
          toast({
            title: "Email Not Confirmed",
            description: "Please check your inbox for a verification email or disable email confirmation in Supabase settings.",
            variant: "destructive"
          });
        } else if (error.message.includes('Invalid login credentials')) {
          toast({
            title: "Login Failed",
            description: "Invalid email or password. Please check your credentials.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Login Error",
            description: error.message || "Failed to log in. Please try again.",
            variant: "destructive"
          });
        }

        throw error;
      }

      if (data.user) {
        console.log("Login successful for user:", data.user.id);

        if (data.session) {
          setSessionExpireTime(data.session.expires_at);
        }

        // Auto-assign user to company groups if this is a widget with companyId
        if (widgetCompanyId) {
          console.log("🏢 Widget login detected, assigning user to company groups:", widgetCompanyId);
          try {
            await companyService.assignUserToCompanyGroups(data.user.id, widgetCompanyId);
            toast({
              title: "Welcome!",
              description: "You've been assigned to your company's content.",
            });
          } catch (error) {
            console.error("Failed to assign user to company groups:", error);
            // Don't fail the login for this - just log it
          }
        }

        handleLoginSuccess(data.user.user_metadata?.name);

        try {
          await recordLoginSession(data.user.id, { source: 'auth_context_password' });
        } catch (e) {
          console.error("Failed to log session:", e);
        }

        // This is the crucial change: redirecting from the auth context
        // after the user session is confirmed.
        navigate(redirectTo);

      } else {
        throw new Error("Login succeeded but no user data was returned.");
      }
    } catch (error) {
      console.error("Login process failed", error);
      setCurrentUser(null);
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = async (navigate: NavigateFunction) => {
    console.log("🚪 Attempting robust logout with ghost session cleanup");
    setIsLoggingOut(true);

    // Capture widget context BEFORE any cleanup that might affect it
    const isCurrentlyWidget = isWidget;
    const currentWidgetCompanyId = widgetCompanyId;

    // ALSO capture from URL params as backup in case context gets lost
    const urlParams = new URLSearchParams(window.location.search);
    const urlCompanyId = urlParams.get('companyId');
    const urlTargetId = urlParams.get('targetId');

    // ALSO check sessionStorage directly as triple backup
    const storedContext = sessionStorage.getItem('widget_context');
    let storedCompanyId = null;
    let storedTargetId = null;
    if (storedContext) {
      try {
        const parsed = JSON.parse(storedContext);
        storedCompanyId = parsed.companyId;
        storedTargetId = parsed.targetId;
      } catch (e) {
        console.warn("Failed to parse widget_context from sessionStorage:", e);
      }
    }

    console.log("🔍 Widget context captured from all sources:", {
      fromHook: { isCurrentlyWidget, currentWidgetCompanyId },
      fromUrl: { urlCompanyId, urlTargetId },
      fromStorage: { storedCompanyId, storedTargetId },
      currentUrl: window.location.href,
      currentPath: window.location.pathname
    });

    try {
      // Try standard logout first, but don't fail if it errors
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.warn("Standard logout failed (will force cleanup):", error);
      }
    } catch (error) {
      console.warn("Exception during logout (will force cleanup):", error);
    }

    // Tracking: update logout time
    try {
      await recordLogoutSession();
    } catch (e) {
      console.error("Failed to update tracking logout:", e);
    }

    // FORCE CLEANUP: Remove all Supabase session data from localStorage
    // This fixes the ghost session issue for widget users
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') || key.startsWith('supabase')) {
          localStorage.removeItem(key);
          console.log(`Cleared ghost session data: ${key}`);
        }
      });

      // Also clear sessionStorage BUT preserve widget context
      Object.keys(sessionStorage).forEach(key => {
        if ((key.startsWith('sb-') || key.startsWith('supabase')) && key !== 'widget_context') {
          sessionStorage.removeItem(key);
          console.log(`Cleared session storage: ${key}`);
        }
      });
      console.log("✅ Widget context preserved during logout cleanup");
    } catch (storageError) {
      console.warn("Error clearing storage:", storageError);
    }

    // Update application state
    setCurrentUser(null);
    setSessionExpireTime(null);
    // Clear React Query cache to prevent stale data from leaking to the next user session.
    queryClient.clear();
    handleLogoutSuccess();

    console.log("Ghost session cleanup complete");

    // Widget-specific cleanup
    if (isCurrentlyWidget) {
      console.log("Widget logout: resizing iframe back to login size");
      window.parent.postMessage({
        type: 'MARKETPOWER_RESIZE',
        height: '700px'
      }, '*');
    }

    // Use smart redirect logic - check all sources for companyId
    const finalCompanyId = currentWidgetCompanyId || urlCompanyId || storedCompanyId;
    const finalTargetId = urlTargetId || storedTargetId;
    const finalIsWidget = isCurrentlyWidget || !!urlCompanyId || !!storedCompanyId;

    console.log("🎯 Final widget context for redirect:", {
      finalCompanyId,
      finalTargetId,
      finalIsWidget
    });

    if (finalCompanyId && finalIsWidget) {
      console.log("🏢 Widget logout: redirecting to branded home page with companyId", finalCompanyId);
      let widgetHomeUrl = `/?companyId=${finalCompanyId}`;
      console.log("📍 Widget logout redirect target:", widgetHomeUrl);
      console.log("🔄 Using window.location.href to prevent AuthenticatedLayout override");
      // Use window.location.href for widget to prevent AuthenticatedLayout Navigate from overriding
      window.location.href = widgetHomeUrl;
    } else {
      console.log("👤 Regular logout: redirecting to standard login page (no widget context found)");
      navigate('/login', { replace: true });
    }

    setAuthLoading(false);
    setIsLoggingOut(false);
  };

  return {
    refreshSession,
    login,
    logout
  };
};
